import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { UserId } from '@/portainer/users/types';
import { withError, withInvalidate } from '@/react-tools/react-query';

import { Team, TeamRole } from '../types';

import { createTeamMembership } from './useAddMemberMutation';
import { buildUrl } from './build-url';
import { teamQueryKeys } from './query-keys';

interface CreatePayload {
  name: string;
  leaders: UserId[];
  denyPortainerAccess: boolean;
}

export function useAddTeamMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTeam,
    ...withError('Failed to create team'),
    ...withInvalidate(queryClient, [teamQueryKeys.base()]),
  });
}

async function createTeam({
  name,
  leaders,
  denyPortainerAccess,
}: CreatePayload): Promise<Team> {
  try {
    const { data: team } = await axios.post<Team>(buildUrl(), {
      name,
      denyPortainerAccess,
    });
    await Promise.all(
      leaders.map((leaderId) =>
        createTeamMembership(leaderId, team.Id, TeamRole.Leader)
      )
    );
    return team;
  } catch (e) {
    throw parseAxiosError(e as Error, 'Unable to create team');
  }
}
